import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from './Button';
import { Link } from 'react-router-dom';

export const AboutCTA: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section className="py-12 md:py-20 bg-white relative overflow-hidden" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div
          className="relative rounded-[40px] overflow-hidden shadow-2xl"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8 }}
        >
          {/* Background Image */}
          <div 
            className="absolute inset-0 z-0"
            style={{
              backgroundImage: `url(/Images/about_CTA.webp)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent z-10" />

          {/* Content */}
          <div className="relative z-20 py-12 md:py-20 px-4 md:px-8 lg:px-16">
            <div className="max-w-3xl">
              <motion.h2
                className="font-heading font-bold mb-6"
                style={{
                  fontFamily: 'Nanum Myeongjo',
                  fontSize: 'clamp(36px, 5vw, 64px)',
                  lineHeight: '1.2',
                  color: '#FFFFFF',
                  letterSpacing: '0%'
                }}
                initial={{ opacity: 0, x: -50 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Eco-friendly boards and recyclable, responsible packaging.
              </motion.h2>

              <motion.p
                className="text-base md:text-xl lg:text-2xl text-gray-200 mb-6 md:mb-10 font-instrument max-w-2xl"
                initial={{ opacity: 0, x: -50 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Advanced printing, precision die-cutting, and high-speed finishing
              </motion.p>

              <Link to="/technology" className="flex flex-wrap items-center gap-6">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button 
                    className="text-white rounded-full px-6 md:px-10 py-3 md:py-4 h-auto font-instrument font-medium text-sm md:text-lg hover:shadow-2xl transition-all duration-300"
                    style={{ background: 'linear-gradient(88.69deg, #CA1167 -1.27%, #F47749 98.89%)' }}
                  >
                    Know More About Our Machinery
                  </Button>
                </motion.div>

                <motion.button
                  className="w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center text-white hover:shadow-xl transition-all duration-300"
                  style={{ backgroundColor: '#F47649' }}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  animate={{
                    boxShadow: [
                      '0 0 0 0 rgba(202, 17, 103, 0.4)',
                      '0 0 0 20px rgba(202, 17, 103, 0)',
                    ]
                  }}
                  transition={{
                    boxShadow: {
                      duration: 2,
                      repeat: Infinity,
                    }
                  }}
                >
                  <ArrowRight size={20} className="md:w-6 md:h-6" />
                </motion.button>
              </Link>

              {/* Stats */}
              
            </div>
          </div>

          {/* Animated decorative elements */}
          <motion.div
            className="absolute top-10 right-10 w-20 h-20 rounded-full bg-gradient-to-br from-[#CA1167]/30 to-[#F47749]/30 blur-2xl"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />
          <motion.div
            className="absolute bottom-20 right-40 w-32 h-32 rounded-full bg-gradient-to-br from-[#F47749]/30 to-[#CA1167]/30 blur-2xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }}
          />
        </motion.div>
      </div>
    </section>
  );
};
